import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Modal,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/Feather';
import { showMessage } from 'react-native-flash-message';
import { useChats, sendChatMessage } from '../hooks/useProfile';
import { Colors, Typography, Spacing, Radius } from '../theme';
import type { ChatItem, TonyteActivity } from '../types';
import { getActivityVenueLabel, getEventShareLink, getLocalizedActivityTime } from '../utils/activityActions';
import { parseActivityDate } from '../utils/activityDates';
import { resolveActivityImage } from '../utils/activityImages';
import { buildShareActionId, SHARE_TYPES, trackEventShare } from '../utils/eventAnalytics';
import { getResolvedEventId } from '../utils/eventIds';
import { useI18n } from '../i18n';
import { useTranslatedActivity } from '../hooks/useTranslatedActivity';

interface Props {
  activity: TonyteActivity;
  userId: string;
  userName: string;
  userPhotoURL?: string;
  visible: boolean;
  onClose: () => void;
}

const ProfileImage: React.FC<{ uri?: string }> = ({ uri }) => {
  const [err, setErr] = useState(false);
  return err || !uri
    ? <View style={ss.avatarFallback}><Icon name="user" size={14} color={Colors.textMuted} /></View>
    : <Image source={{ uri }} style={ss.avatar} onError={() => setErr(true)} />;
};

export const EventShareSheet: React.FC<Props> = React.memo(({
  activity,
  userId,
  userName,
  userPhotoURL,
  visible,
  onClose,
}) => {
  const { t, tp } = useI18n();
  const translatedActivity = useTranslatedActivity(activity);
  const insets = useSafeAreaInsets();
  // Avoid opening chat listeners for every event detail screen while this
  // modal is hidden. They are only needed after the user opens Share.
  const { chats, loading } = useChats(visible ? userId : null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [coverUri, setCoverUri] = useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (!visible) { setSearch(''); setSelected(new Set()); return; }
    resolveActivityImage(activity).then(resolved => {
      if (typeof resolved.source === 'string') setCoverUri(resolved.source);
    }).catch(() => {});
  }, [activity, visible]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.eventTitle?.toLowerCase().includes(q) ||
      c.planTitle?.toLowerCase().includes(q),
    );
  }, [chats, search]);

  const toggle = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSend = useCallback(async () => {
    if (selected.size === 0 || sending) return;
    setSending(true);
    const resolvedEventId = getResolvedEventId(activity);
    const { endAt } = parseActivityDate(activity);
    const sharedEvent = {
      eventId: resolvedEventId,
      title: translatedActivity.title || activity.title,
      location: getActivityVenueLabel(activity) || activity.address || activity.city || '',
      time: getLocalizedActivityTime(activity),
      teaser: translatedActivity.description?.trim() || t('event.foundOnTonyte'),
      imageUrl: coverUri ?? '',
      link: getEventShareLink(activity),
      // Snapshot only — see ChatMessage.sharedEvent.endAt for why this can
      // never be the actual navigation gate.
      endAt: endAt ? endAt.toISOString() : undefined,
    };
    try {
      await Promise.all(
        [...selected].map(chatId => {
          const chat = chats.find(c => c.id === chatId);
          return sendChatMessage({
            chatId,
            kind: chat?.kind ?? 'match',
            text: '',
            senderId: userId,
            senderName: userName,
            senderPhotoURL: userPhotoURL,
            sharedEvent,
          }).then(() => {
            // Tracked per-chat, right when THAT chat's message doc lands —
            // not after the overall Promise.all — so sharing into two
            // chats counts twice even if one of the OTHER sends in this
            // same batch later fails and rejects the outer Promise.all.
            trackEventShare({
              eventId: resolvedEventId,
              organizerId: activity.hostId || null,
              shareType: SHARE_TYPES.INTERNAL_CHAT,
              destinationChatId: chatId,
              shareActionId: buildShareActionId(resolvedEventId, chatId),
            });
          });
        }),
      );
      showMessage({ message: tp('share.sentToChatsCount', selected.size), type: 'success', duration: 2000 });
      onClose();
    } catch {
      showMessage({ message: t('share.couldNotSend'), type: 'danger', duration: 2500 });
    } finally {
      setSending(false);
    }
  }, [activity, chats, coverUri, onClose, selected, sending, t, tp, translatedActivity.description, translatedActivity.title, userId, userName, userPhotoURL]);

  const renderItem = ({ item }: { item: ChatItem }) => {
    const isSelected = selected.has(item.id);
    const displayName = item.eventTitle || item.planTitle || item.name || t('chat.title');
    return (
      <TouchableOpacity
        style={[ss.chatRow, isSelected && ss.chatRowSelected]}
        onPress={() => toggle(item.id)}
        activeOpacity={0.75}
      >
        <ProfileImage uri={item.photo} />
        <View style={ss.chatInfo}>
          <Text style={ss.chatName} numberOfLines={1}>{displayName}</Text>
          {item.lastMsg ? (
            <Text style={ss.chatLast} numberOfLines={1}>{item.lastMsg}</Text>
          ) : null}
        </View>
        <View style={[ss.checkbox, isSelected && ss.checkboxSelected]}>
          {isSelected && <Icon name="check" size={12} color="#fff" />}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={ss.overlay}>
        <View style={[ss.sheet, { paddingBottom: insets.bottom + Spacing.base }]}>
          {/* Header */}
          <View style={ss.header}>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Icon name="x" size={20} color={Colors.textPrimary} />
            </TouchableOpacity>
            <Text style={ss.headerTitle}>{t('event.sendInTonyteChat')}</Text>
            <TouchableOpacity
              style={[ss.sendBtn, (selected.size === 0 || sending) && ss.sendBtnDisabled]}
              onPress={handleSend}
              disabled={selected.size === 0 || sending}
            >
              {sending
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={ss.sendBtnText}>{t('common.send')}{selected.size > 0 ? ` (${selected.size})` : ''}</Text>}
            </TouchableOpacity>
          </View>

          {/* Event preview */}
          <View style={ss.preview}>
            {coverUri
              ? <Image source={{ uri: coverUri }} style={ss.previewImage} />
              : <View style={[ss.previewImage, ss.previewImageFallback]}><Icon name="calendar" size={20} color={Colors.textMuted} /></View>}
            <View style={ss.previewInfo}>
              <Text style={ss.previewTitle} numberOfLines={2}>{translatedActivity.translatingTitle ? '...' : translatedActivity.title}</Text>
              <Text style={ss.previewMeta} numberOfLines={1}>{getLocalizedActivityTime(activity)}</Text>
              {getActivityVenueLabel(activity) ? (
                <Text style={ss.previewMeta} numberOfLines={1}>{getActivityVenueLabel(activity)}</Text>
              ) : null}
            </View>
          </View>

          {/* Search */}
          <View style={ss.searchWrap}>
            <Icon name="search" size={15} color={Colors.textMuted} style={ss.searchIcon} />
            <TextInput
              style={ss.searchInput}
              placeholder={t('chat.searchChats')}
              placeholderTextColor={Colors.textMuted}
              value={search}
              onChangeText={setSearch}
              autoCorrect={false}
            />
          </View>

          {/* Chat list */}
          {loading ? (
            <View style={ss.loadingWrap}>
              <ActivityIndicator color={Colors.sunset} />
            </View>
          ) : filtered.length === 0 ? (
            <View style={ss.loadingWrap}>
              <Text style={ss.emptyText}>{search ? t('chat.noChatsFound') : t('chat.noChatsYet')}</Text>
            </View>
          ) : (
            <FlatList
              data={filtered}
              keyExtractor={item => item.id}
              renderItem={renderItem}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={ss.listContent}
            />
          )}
        </View>
      </View>
    </Modal>
  );
});

const ss = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: Colors.bgCard,
    borderTopLeftRadius: Radius.xxl,
    borderTopRightRadius: Radius.xxl,
    maxHeight: '80%',
    minHeight: 340,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.base,
    paddingVertical: Spacing.base,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  sendBtn: {
    backgroundColor: Colors.sunset,
    borderRadius: Radius.xl,
    paddingHorizontal: Spacing.base,
    paddingVertical: 7,
    minWidth: 60,
    alignItems: 'center',
  },
  sendBtnDisabled: { opacity: 0.45 },
  sendBtnText: { color: '#fff', fontSize: Typography.sm, fontWeight: Typography.semibold },
  preview: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    margin: Spacing.base,
    padding: Spacing.sm,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: Radius.lg,
  },
  previewImage: {
    width: 56,
    height: 56,
    borderRadius: Radius.md,
    backgroundColor: Colors.bgCardAlt,
  },
  previewImageFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewInfo: { flex: 1 },
  previewTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
    marginBottom: 2,
  },
  previewMeta: { color: Colors.textMuted, fontSize: Typography.xs },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.bgInput,
    borderRadius: Radius.xl,
    marginHorizontal: Spacing.base,
    marginBottom: Spacing.sm,
    paddingHorizontal: Spacing.sm,
    height: 40,
  },
  searchIcon: { marginRight: 6 },
  searchInput: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    height: 40,
  },
  listContent: { paddingHorizontal: Spacing.sm, paddingBottom: Spacing.base },
  chatRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingVertical: 10,
    paddingHorizontal: Spacing.sm,
    borderRadius: Radius.lg,
    marginBottom: 2,
  },
  chatRowSelected: { backgroundColor: 'rgba(242,125,38,0.1)' },
  avatar: { width: 42, height: 42, borderRadius: 21 },
  avatarFallback: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: Colors.bgCardAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatInfo: { flex: 1 },
  chatName: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
    marginBottom: 2,
  },
  chatLast: { color: Colors.textMuted, fontSize: Typography.xs },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: Colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: Colors.sunset,
    borderColor: Colors.sunset,
  },
  loadingWrap: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: Spacing.xxxl },
  emptyText: { color: Colors.textMuted, fontSize: Typography.sm },
});

export default EventShareSheet;
